'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Video, MOCK_VIDEOS, formatViews } from '@/data/videos';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import AuthModal from '@/components/AuthModal';
import VideoAdPlayer from '@/components/VideoAdPlayer';

interface VideoPlayerModalProps {
  video: Video | null;
  onClose: () => void;
  allVideos?: Video[];
  onVideoSelect?: (video: Video) => void;
}

interface Comment {
  id: string;
  video_id: string;
  parent_id: string | null;
  author: string;
  body: string;
  likes: number;
  created_at: string;
  updated_at?: string | null;
  deleted_at?: string | null;
  is_deleted?: boolean;
  user_id?: string | null;
  avatar_url?: string | null;
  replies?: Comment[];
}

function formatTimestamp(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}

function extractYouTubeId(url: string): string | null {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

function extractVimeoId(url: string): string | null {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

export default function VideoPlayerModal({ video, onClose, allVideos, onVideoSelect }: VideoPlayerModalProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentText, setCommentText] = useState('');
  const [expandedReplies, setExpandedReplies] = useState<Set<string>>(new Set());
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [totalComments, setTotalComments] = useState(0);
  const [loadingComments, setLoadingComments] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [anonName, setAnonName] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Video like state
  const [videoLikeCount, setVideoLikeCount] = useState(0);
  const [videoLiked, setVideoLiked] = useState(false);
  const [videoLikeLoading, setVideoLikeLoading] = useState(false);

  // Report/Flag state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportCategory, setReportCategory] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  // Comment like state: map of commentId -> { count, liked }
  const [commentLikes, setCommentLikes] = useState<Record<string, { count: number; liked: boolean }>>({});
  const [commentLikeLoading, setCommentLikeLoading] = useState<string | null>(null);

  const { user, profile } = useAuth();
  const supabase = createClient();

  const [relatedSort, setRelatedSort] = useState<'category' | 'date' | 'views'>('category');
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Ad state — show pre-roll before main video
  const [showAd, setShowAd] = useState(true);
  const [adPlacement, setAdPlacement] = useState<'pre-roll' | 'between-videos'>('pre-roll');
  const handleAdComplete = useCallback(() => setShowAd(false), []);
  const prevVideoIdRef = useRef<string | null>(null);

  // Reset ad when a new video is opened
  useEffect(() => {
    if (!video) return;
    // If no previous video (fresh open), use pre-roll; switching uses between-videos (set by handleRecommendedClick)
    if (!prevVideoIdRef.current) {
      setAdPlacement('pre-roll');
    }
    prevVideoIdRef.current = video.id;
    setShowAd(true);
  }, [video?.id]);

  // Clear prevVideoIdRef when modal closes
  useEffect(() => {
    if (!video) {
      prevVideoIdRef.current = null;
      setAdPlacement('pre-roll');
    }
  }, [video]);

  // Compute related videos based on sort mode
  const videoPool = allVideos && allVideos.length > 0 ? allVideos : MOCK_VIDEOS;

  const getRelatedVideos = useCallback(() => {
    if (!video) return [];
    const others = videoPool.filter((v) => v.id !== video.id && v.status === 'approved');

    if (relatedSort === 'category') {
      const sameCategory = others
        .filter((v) => v.category === video.category)
        .sort((a, b) => b.views - a.views);
      const different = others
        .filter((v) => v.category !== video.category)
        .sort((a, b) => b.views - a.views);
      const combined = [...sameCategory, ...different];
      return combined.slice(0, 10);
    }
    if (relatedSort === 'date') {
      return [...others]
        .sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime())
        .slice(0, 10);
    }
    // views
    return [...others].sort((a, b) => b.views - a.views).slice(0, 10);
  }, [video, videoPool, relatedSort]);

  const relatedVideos = getRelatedVideos();

  const updateScrollButtons = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener('scroll', updateScrollButtons, { passive: true });
    return () => el.removeEventListener('scroll', updateScrollButtons);
  }, [relatedVideos, updateScrollButtons]);

  // Reset carousel scroll when video changes
  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = 0;
    }
    updateScrollButtons();
  }, [video?.id, relatedSort]);

  const scrollCarousel = (dir: 'left' | 'right') => {
    const el = carouselRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.75;
    el.scrollBy({ left: dir === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
  };

  // Fetch video likes
  const fetchVideoLikes = useCallback(async (videoId: string) => {
    const { count } = await supabase
      .from('video_likes')
      .select('*', { count: 'exact', head: true })
      .eq('video_id', videoId);
    setVideoLikeCount(count ?? 0);

    if (user) {
      const { data } = await supabase
        .from('video_likes')
        .select('id')
        .eq('video_id', videoId)
        .eq('user_id', user.id)
        .maybeSingle();
      setVideoLiked(!!data);
    } else {
      setVideoLiked(false);
    }
  }, [user]);

  // Fetch comment likes for a list of comment IDs
  const fetchCommentLikes = useCallback(async (commentIds: string[]) => {
    if (commentIds.length === 0) return;

    const { data: counts } = await supabase
      .from('comment_likes')
      .select('comment_id')
      .in('comment_id', commentIds);

    const countMap: Record<string, number> = {};
    (counts || []).forEach((row: { comment_id: string }) => {
      countMap[row.comment_id] = (countMap[row.comment_id] || 0) + 1;
    });

    let likedSet = new Set<string>();
    if (user) {
      const { data: userLikes } = await supabase
        .from('comment_likes')
        .select('comment_id')
        .in('comment_id', commentIds)
        .eq('user_id', user.id);
      likedSet = new Set((userLikes || []).map((r: { comment_id: string }) => r.comment_id));
    }

    setCommentLikes((prev) => {
      const next = { ...prev };
      commentIds.forEach((id) => {
        next[id] = { count: countMap[id] ?? 0, liked: likedSet.has(id) };
      });
      return next;
    });
  }, [user]);

  const fetchComments = useCallback(async (videoId: string) => {
    setLoadingComments(true);
    try {
      const { data, error } = await supabase
        .from('comments')
        .select('*')
        .eq('video_id', videoId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Failed to load comments:', error.message);
        setLoadingComments(false);
        return;
      }

      const rows: Comment[] = data || [];
      const topLevel = rows.filter((c) => !c.parent_id);
      const replies = rows.filter((c) => c.parent_id);

      const threaded = topLevel.map((c) => ({
        ...c,
        replies: replies.filter((r) => r.parent_id === c.id).reverse(),
      }));

      setComments(threaded);
      setTotalComments(rows.length);

      // Fetch likes for all comments
      const allIds = rows.map((c) => c.id);
      fetchCommentLikes(allIds);
    } catch (err) {
      console.error('Unexpected error loading comments:', err);
    } finally {
      setLoadingComments(false);
    }
  }, [fetchCommentLikes]);

  useEffect(() => {
    setIsLoaded(false);
    setComments([]);
    setTotalComments(0);
    setVideoLikeCount(0);
    setVideoLiked(false);
    setCommentLikes({});
    if (video) {
      fetchComments(video.id);
      fetchVideoLikes(video.id);
    }
    if (typeof document !== 'undefined') {
      document.body.style.overflow = video ? 'hidden' : '';
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
      }
    };
  }, [video, fetchComments, fetchVideoLikes]);

  // Real-time subscription for comments
  useEffect(() => {
    if (!video) return;
    const channel = supabase
      .channel(`modal_comments_${video.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'comments', filter: `video_id=eq.${video.id}` },
        (payload) => {
          const newComment = payload.new as Comment;
          if (newComment.is_deleted) return;
          setComments((prev) => {
            if (newComment.parent_id) {
              return prev.map((c) =>
                c.id === newComment.parent_id
                  ? {
                      ...c,
                      replies: [...(c.replies || []).filter((r) => r.id !== newComment.id), { ...newComment, replies: [] }],
                    }
                  : c
              );
            } else {
              if (prev.find((c) => c.id === newComment.id)) return prev;
              return [{ ...newComment, replies: [] }, ...prev];
            }
          });
          setTotalComments((prev) => prev + 1);
          setCommentLikes((prev) => ({ ...prev, [newComment.id]: { count: 0, liked: false } }));
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'comments', filter: `video_id=eq.${video.id}` },
        (payload) => {
          const updated = payload.new as Comment;
          setComments((prev) =>
            prev.map((c) => {
              if (c.id === updated.id) return { ...c, ...updated, replies: c.replies };
              return {
                ...c,
                replies: (c.replies || []).map((r) =>
                  r.id === updated.id ? { ...r, ...updated } : r
                ),
              };
            })
          );
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [video?.id, supabase]);

  // Real-time subscription for video likes
  useEffect(() => {
    if (!video) return;
    const channel = supabase
      .channel(`modal_video_likes_${video.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'video_likes', filter: `video_id=eq.${video.id}` },
        () => { fetchVideoLikes(video.id); }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [video?.id, fetchVideoLikes]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', handleKey);
      return () => window.removeEventListener('keydown', handleKey);
    }
  }, [onClose]);

  if (!video) return null;

  const getEmbedUrl = () => {
    if (video.source === 'youtube') {
      const ytId = video.embedId || extractYouTubeId(video.url);
      if (ytId) return `https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`;
    }
    if (video.source === 'vimeo') {
      const vimeoId = video.embedId || extractVimeoId(video.url);
      if (vimeoId) return `https://player.vimeo.com/video/${vimeoId}?autoplay=1`;
    }
    // Also try to detect YouTube/Vimeo from URL regardless of source field
    if (!video.embedId) {
      const ytId = extractYouTubeId(video.url);
      if (ytId) return `https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`;
      const vimeoId = extractVimeoId(video.url);
      if (vimeoId) return `https://player.vimeo.com/video/${vimeoId}?autoplay=1`;
    }
    return null;
  };

  const embedUrl = getEmbedUrl();

  const isStorageVideo =
    (video.source === 'upload' || video.source === 'file') &&
    (video.url.startsWith('http://') || video.url.startsWith('https://')) &&
    !embedUrl;

  const handleVideoLike = async () => {
    if (!user) { setShowAuthPrompt(true); return; }
    if (videoLikeLoading) return;
    setVideoLikeLoading(true);
    try {
      if (videoLiked) {
        await supabase
          .from('video_likes')
          .delete()
          .eq('video_id', video.id)
          .eq('user_id', user.id);
        setVideoLiked(false);
        setVideoLikeCount((prev) => Math.max(0, prev - 1));
      } else {
        await supabase
          .from('video_likes')
          .insert({ video_id: video.id, user_id: user.id });
        setVideoLiked(true);
        setVideoLikeCount((prev) => prev + 1);
      }
    } finally {
      setVideoLikeLoading(false);
    }
  };

  const handleSubmitReport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reportCategory) return;
    setReportSubmitting(true);
    setReportError(null);
    try {
      const { error } = await supabase
        .from('video_reports')
        .insert({
          video_id: video!.id,
          category: reportCategory,
          details: reportDetails.trim() || null,
          reporter_id: user?.id ?? null,
          reporter_email: user?.email ?? null,
        });
      if (error) {
        // If table doesn't exist yet, still show success to user
        if (error.code !== '42P01') {
          setReportError('Failed to submit report. Please try again.');
          return;
        }
      }
      setReportSuccess(true);
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleCloseReport = () => {
    setShowReportModal(false);
    setReportCategory('');
    setReportDetails('');
    setReportSuccess(false);
    setReportError(null);
  };

  const handleCommentLike = async (commentId: string) => {
    if (!user) { setShowAuthPrompt(true); return; }
    if (commentLikeLoading === commentId) return;
    setCommentLikeLoading(commentId);
    const current = commentLikes[commentId] ?? { count: 0, liked: false };
    try {
      if (current.liked) {
        await supabase
          .from('comment_likes')
          .delete()
          .eq('comment_id', commentId)
          .eq('user_id', user.id);
        setCommentLikes((prev) => ({
          ...prev,
          [commentId]: { count: Math.max(0, (prev[commentId]?.count ?? 1) - 1), liked: false },
        }));
      } else {
        await supabase
          .from('comment_likes')
          .insert({ comment_id: commentId, user_id: user.id });
        setCommentLikes((prev) => ({
          ...prev,
          [commentId]: { count: (prev[commentId]?.count ?? 0) + 1, liked: true },
        }));
      }
    } finally {
      setCommentLikeLoading(null);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const displayName = user
        ? (profile?.full_name || user.email?.split('@')[0] || 'Anonymous')
        : (anonName.trim() || 'Anonymous');
      const avatarUrl = user ? (profile?.avatar_url || null) : null;
      const { data, error } = await supabase
        .from('comments')
        .insert({
          video_id: video!.id,
          parent_id: null,
          author: displayName,
          body: commentText.trim(),
          likes: 0,
          user_id: user?.id ?? null,
          avatar_url: avatarUrl,
        })
        .select()
        .single();

      if (error) {
        setSubmitError('Failed to post comment. Please try again.');
        return;
      }

      const newComment: Comment = { ...data, replies: [] };
      setComments((prev) => {
        if (prev.find((c) => c.id === newComment.id)) return prev;
        return [newComment, ...prev];
      });
      setTotalComments((prev) => prev + 1);
      setCommentText('');
      setCommentLikes((prev) => ({ ...prev, [data.id]: { count: 0, liked: false } }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReply = async (e: React.FormEvent, parentId: string) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const displayName = user
        ? (profile?.full_name || user.email?.split('@')[0] || 'Anonymous')
        : (anonName.trim() || 'Anonymous');
      const avatarUrl = user ? (profile?.avatar_url || null) : null;
      const { data, error } = await supabase
        .from('comments')
        .insert({
          video_id: video!.id,
          parent_id: parentId,
          author: displayName,
          body: replyText.trim(),
          likes: 0,
          user_id: user?.id ?? null,
          avatar_url: avatarUrl,
        })
        .select()
        .single();

      if (error) {
        setSubmitError('Failed to post reply. Please try again.');
        return;
      }

      const newReply: Comment = { ...data, replies: [] };
      setComments((prev) =>
        prev.map((c) =>
          c.id === parentId ? { ...c, replies: [...(c.replies || []).filter((r) => r.id !== newReply.id), newReply] } : c
        )
      );
      setTotalComments((prev) => prev + 1);
      setReplyText('');
      setReplyingTo(null);
      setCommentLikes((prev) => ({ ...prev, [data.id]: { count: 0, liked: false } }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string, parentId: string | null) => {
    if (!user) return;
    setDeletingId(commentId);
    try {
      // Soft-delete: set is_deleted=true and deleted_at timestamp
      const { error } = await supabase
        .from('comments')
        .update({ is_deleted: true, deleted_at: new Date().toISOString() })
        .eq('id', commentId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Delete error:', error.message);
        return;
      }

      const markDeleted = (c: Comment): Comment => ({
        ...c,
        is_deleted: true,
        deleted_at: new Date().toISOString(),
        body: '[deleted]',
      });

      if (parentId) {
        // Remove reply from parent's replies array
        setComments((prev) =>
          prev.map((c) =>
            c.id === parentId
              ? { ...c, replies: (c.replies || []).filter((r) => r.id !== commentId) }
              : c
          )
        );
        // Update the deleted reply in the parent's replies array
        setComments((prev) =>
          prev.map((c) =>
            c.id === parentId
              ? {
                  ...c,
                  replies: (c.replies || []).map((r) =>
                    r.id === commentId ? markDeleted(r) : r
                  ),
                }
              : c
          )
        );
        setTotalComments((prev) => prev - 1);
      } else {
        // Remove top-level comment and count its replies
        const target = comments.find((c) => c.id === commentId);
        const replyCount = target?.replies?.length ?? 0;
        setComments((prev) => prev.filter((c) => c.id !== commentId));
        // Update the deleted comment in the top-level comments array
        setComments((prev) =>
          prev.map((c) =>
            c.id === commentId ? markDeleted(c) : c
          )
        );
        setTotalComments((prev) => prev - 1 - replyCount);
      }
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const handleStartEdit = (comment: Comment) => {
    setEditingId(comment.id);
    setEditText(comment.body);
  };

  const handleSaveEdit = async (commentId: string, parentId: string | null) => {
    if (!user || !editText.trim()) return;
    setSavingEdit(true);
    try {
      const { error } = await supabase
        .from('comments')
        .update({ body: editText.trim(), updated_at: new Date().toISOString() })
        .eq('id', commentId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Edit error:', error.message);
        return;
      }

      const applyEdit = (c: Comment): Comment => ({
        ...c,
        body: editText.trim(),
        updated_at: new Date().toISOString(),
      });

      if (parentId) {
        setComments((prev) =>
          prev.map((c) =>
            c.id === parentId
              ? { ...c, replies: (c.replies || []).map((r) => r.id === commentId ? applyEdit(r) : r) }
              : c
          )
        );
      } else {
        setComments((prev) =>
          prev.map((c) => c.id === commentId ? { ...applyEdit(c), replies: c.replies } : c)
        );
      }
      setEditingId(null);
      setEditText('');
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleReplies = (commentId: string) => {
    setExpandedReplies((prev) => {
      const next = new Set(prev);
      if (next.has(commentId)) next.delete(commentId);
      else next.add(commentId);
      return next;
    });
  };

  const handleRecommendedClick = (rec: Video) => {
    if (onVideoSelect) {
      setAdPlacement('between-videos');
      onVideoSelect(rec);
    }
  };

  return (
    <>
      <div
        className="modal-overlay !items-start !overflow-y-auto"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="w-full max-w-4xl animate-slide-up my-auto flex flex-col px-2 sm:px-4 py-2 sm:py-4">
          {/* Close button */}
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <div className="flex items-center gap-2">
              {video.tag && (
                <span className="text-brand-red font-bold uppercase text-sm">{video.tag}</span>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-text-secondary hover:text-text-primary transition-colors duration-150 bg-surface-secondary border border-stroke-primary rounded-[4px] p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Close video player"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Video Player */}
          <div className="bg-surface-tertiary border border-stroke-primary rounded-[4px] overflow-hidden">
            {showAd ? (
              <VideoAdPlayer placement={adPlacement} onAdComplete={handleAdComplete} videoCategory={video.category} />
            ) : embedUrl ? (
              <div className="relative" style={{ paddingTop: '56.25%' }}>
                {!isLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-surface-primary">
                    <div className="w-10 h-10 border-2 border-brand-red border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                <iframe
                  src={embedUrl}
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  onLoad={() => setIsLoaded(true)}
                  title={video.title}
                />
              </div>
            ) : isStorageVideo ? (
              <div className="relative" style={{ paddingTop: '56.25%' }}>
                {!isLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-surface-primary">
                    <div className="w-10 h-10 border-2 border-brand-red border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                <video
                  src={video.url}
                  className="absolute inset-0 w-full h-full"
                  controls
                  autoPlay
                  onLoadedData={() => setIsLoaded(true)}
                  onError={() => setIsLoaded(true)}
                  title={video.title}
                  style={{ display: isLoaded ? 'block' : 'none' }}
                />
              </div>
            ) : (
              <div className="relative" style={{ paddingTop: '56.25%' }}>
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-surface-secondary">
                  <svg className="w-16 h-16 text-text-muted mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <p className="text-text-secondary text-base">Video preview not available</p>
                  <p className="text-text-muted text-sm mt-1">{video.url}</p>
                </div>
              </div>
            )}
          </div>

          {/* Video Info */}
          <div className="bg-surface-secondary border border-stroke-primary border-t-0 rounded-b-[4px] px-3 sm:px-5 py-3 sm:py-4">
            <h2 className="text-text-primary text-base sm:text-lg font-normal leading-snug mb-2 sm:mb-3">
              {video.tag && <span className="tag-label mr-1">{video.tag} </span>}
              {video.title}
            </h2>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-3 sm:gap-4 text-text-secondary text-xs sm:text-sm flex-wrap">
                <span className="flex items-center gap-1 sm:gap-1.5">
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {video.views.toLocaleString()} views
                </span>
                <span className="flex items-center gap-1 sm:gap-1.5">
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  {totalComments.toLocaleString()} comments
                </span>
                {/* Video Like Button */}
                <button
                  onClick={handleVideoLike}
                  disabled={videoLikeLoading}
                  className={`flex items-center gap-1 sm:gap-1.5 transition-colors min-h-[44px] px-1 ${
                    videoLiked
                      ? 'text-brand-red' :'hover:text-brand-red'
                  } disabled:opacity-60`}
                  aria-label={videoLiked ? 'Unlike video' : 'Like video'}
                  title={!user ? 'Sign in to like' : videoLiked ? 'Unlike' : 'Like this video'}
                >
                  <svg
                    className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                    fill={videoLiked ? 'currentColor' : 'none'}
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                  </svg>
                  <span>{videoLikeCount > 0 ? videoLikeCount.toLocaleString() : 'Like'}</span>
                </button>
                {/* Flag/Report Button */}
                <button
                  onClick={() => setShowReportModal(true)}
                  className="flex items-center gap-1 sm:gap-1.5 text-text-secondary hover:text-orange-500 transition-colors min-h-[44px] px-1"
                  aria-label="Report this video"
                  title="Report inappropriate content"
                >
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21V5a1 1 0 011-1h10.586a1 1 0 01.707.293l1.414 1.414A1 1 0 0017.414 6H21a1 1 0 01.894 1.447l-3 6a1 1 0 01-.894.553H3" />
                  </svg>
                  <span className="hidden sm:inline">Report</span>
                </button>
                <span className="text-text-muted hidden sm:inline">by {video.submittedBy}</span>
              </div>
              <span className="bg-surface-tertiary text-text-muted text-xs px-2 py-1 uppercase tracking-wide self-start sm:self-auto">
                {video.category}
              </span>
            </div>
            {video.description && (
              <p className="text-text-secondary text-xs sm:text-sm mt-2 sm:mt-3 leading-relaxed">{video.description}</p>
            )}
          </div>

          {/* ===== RELATED VIDEOS CAROUSEL ===== */}
          {relatedVideos.length > 0 && (
            <div className="bg-surface-primary border border-stroke-primary border-t-0">
              {/* Header with sort controls */}
              <div className="flex items-center justify-between px-3 sm:px-5 py-2 sm:py-3 border-b border-stroke-primary flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-brand-red flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                  <span className="text-text-primary text-xs sm:text-sm font-bold uppercase tracking-wider">
                    Related Videos
                  </span>
                </div>
                {/* Sort pills */}
                <div className="flex items-center gap-1 tap-compact">
                  {([
                    { key: 'category', label: 'Category' },
                    { key: 'views', label: 'Top' },
                    { key: 'date', label: 'Latest' },
                  ] as { key: 'category' | 'views' | 'date'; label: string }[]).map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setRelatedSort(key)}
                      className={`text-xs px-3 py-2 rounded-[4px] font-bold uppercase tracking-wide transition-colors whitespace-nowrap ${
                        relatedSort === key
                          ? 'bg-brand-red text-white' :'bg-surface-secondary text-text-muted hover:text-text-primary border border-stroke-primary'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Carousel container */}
              <div className="relative">
                {/* Left arrow */}
                {canScrollLeft && (
                  <button
                    onClick={() => scrollCarousel('left')}
                    className="absolute left-0 top-0 bottom-0 z-10 w-8 sm:w-10 hidden sm:flex items-center justify-center bg-gradient-to-r from-surface-primary to-transparent hover:from-surface-secondary transition-colors"
                    aria-label="Scroll left"
                  >
                    <svg className="w-5 h-5 text-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}

                {/* Scrollable track */}
                <div
                  ref={carouselRef}
                  className="carousel-scroll flex gap-0 overflow-x-auto"
                  onScroll={updateScrollButtons}
                >
                  {relatedVideos.map((rec) => (
                    <button
                      key={rec.id}
                      onClick={() => handleRecommendedClick(rec)}
                      className="flex-shrink-0 flex flex-col text-left group hover:bg-surface-secondary active:bg-surface-secondary transition-colors duration-150 border-r border-stroke-primary last:border-r-0"
                      style={{ width: 'clamp(140px, 40vw, 180px)', scrollSnapAlign: 'start' }}
                      aria-label={`Watch ${rec.title}`}
                    >
                      {/* Thumbnail */}
                      <div className="relative w-full overflow-hidden" style={{ aspectRatio: '16/9' }}>
                        <img
                          src={rec.thumbnail}
                          alt={rec.thumbnailAlt}
                          className="w-full h-full object-cover group-hover:brightness-75 transition-all duration-300"
                        />
                        {/* Play overlay */}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <div className="w-8 h-8 bg-brand-red bg-opacity-90 rounded-full flex items-center justify-center shadow-lg">
                            <svg className="w-3.5 h-3.5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                        {/* Views badge */}
                        <div className="absolute bottom-1 right-1 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded flex items-center gap-0.5">
                          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          {formatViews(rec.views)}
                        </div>
                        {/* Category badge (shown when not in category sort) */}
                        {relatedSort !== 'category' && (
                          <div className="absolute top-1 left-1 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded uppercase tracking-wide truncate max-w-[100px]">
                            {rec.category}
                          </div>
                        )}
                      </div>
                      {/* Info */}
                      <div className="px-2 py-2 flex-1">
                        {rec.tag && (
                          <span className="text-brand-red font-bold text-xs uppercase tracking-wide">{rec.tag}</span>
                        )}
                        <p className="text-text-primary text-xs leading-snug line-clamp-2 mt-0.5">{rec.title}</p>
                        <p className="text-text-muted text-xs mt-0.5 uppercase tracking-wide truncate">{rec.category}</p>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Right arrow */}
                {canScrollRight && (
                  <button
                    onClick={() => scrollCarousel('right')}
                    className="absolute right-0 top-0 bottom-0 z-10 w-8 sm:w-10 hidden sm:flex items-center justify-center bg-gradient-to-l from-surface-primary to-transparent hover:from-surface-secondary transition-colors"
                    aria-label="Scroll right"
                  >
                    <svg className="w-5 h-5 text-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Comments Section */}
          <div className="bg-surface-primary border border-stroke-primary border-t-0 rounded-b-[4px] mt-0">
            {/* Comment Header */}
            <div className="flex items-center gap-2 px-3 sm:px-5 py-2 sm:py-3 border-b border-stroke-primary">
              <svg className="w-4 h-4 text-brand-red" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="text-text-primary text-xs sm:text-sm font-bold uppercase tracking-wider">
                Comments ({totalComments.toLocaleString()})
              </span>
            </div>

            {/* Submit Comment Form */}
            <div className="px-3 sm:px-5 py-3 sm:py-4 border-b border-stroke-primary">
              <form onSubmit={handleSubmitComment}>
                <div className="flex items-center gap-2 mb-2 tap-compact">
                  {user && profile?.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt="Your avatar"
                      className="flex-shrink-0 w-7 h-7 rounded-full object-cover border border-stroke-primary"
                      onError={(e) => {
                        const target = e.currentTarget as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-red flex items-center justify-center"
                    style={{ display: (user && profile?.avatar_url) ? 'none' : 'flex' }}
                  >
                    <span className="text-white text-xs font-bold uppercase">
                      {user ? (profile?.full_name || user.email || 'U').charAt(0) : (anonName.trim() || 'A').charAt(0)}
                    </span>
                  </div>
                  {user ? (
                    <span className="text-text-secondary text-xs">
                      Commenting as <span className="text-text-primary font-bold">{profile?.full_name || user.email?.split('@')[0]}</span>
                    </span>
                  ) : (
                    <input
                      type="text"
                      value={anonName}
                      onChange={(e) => setAnonName(e.target.value)}
                      placeholder="Your name (optional)"
                      maxLength={50}
                      className="flex-1 bg-surface-secondary border border-stroke-primary text-text-primary text-xs px-2 py-1 rounded-[4px] placeholder-text-muted focus:outline-none focus:border-brand-red transition-colors"
                    />
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Add a comment..."
                    maxLength={500}
                    className="flex-1 bg-surface-secondary border border-stroke-primary text-text-primary text-sm px-3 py-2.5 rounded-[4px] placeholder-text-muted focus:outline-none focus:border-brand-red transition-colors min-w-0"
                  />
                  <button
                    type="submit"
                    disabled={!commentText.trim() || submitting}
                    className="bg-brand-red text-white text-xs sm:text-sm font-bold uppercase px-4 py-2.5 rounded-[4px] hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex-shrink-0 min-h-[44px]"
                  >
                    {submitting ? '...' : 'Post'}
                  </button>
                </div>
                {submitError && (
                  <p className="text-red-500 text-xs mt-1">{submitError}</p>
                )}
              </form>
            </div>

            {/* Comment List */}
            <div className="max-h-72 sm:max-h-96 overflow-y-auto">
              {loadingComments ? (
                <div className="px-5 py-8 flex items-center justify-center gap-2 text-text-muted text-sm">
                  <div className="w-4 h-4 border-2 border-brand-red border-t-transparent rounded-full animate-spin"></div>
                  Loading comments...
                </div>
              ) : comments.length === 0 ? (
                <div className="px-5 py-8 text-center text-text-muted text-sm">
                  No comments yet. Be the first to comment.
                </div>
              ) : (
                <ul className="divide-y divide-stroke-primary">
                  {comments.map((comment) => {
                    const cLike = commentLikes[comment.id] ?? { count: 0, liked: false };
                    return (
                      <li key={comment.id} className="px-3 sm:px-5 py-4">
                        <div className="flex gap-2 sm:gap-3">
                          {comment.avatar_url ? (
                            <img
                              src={comment.avatar_url}
                              alt={`${comment.author}'s avatar`}
                              className="flex-shrink-0 w-8 h-8 rounded-full object-cover border border-stroke-primary"
                              onError={(e) => {
                                const target = e.currentTarget as HTMLImageElement;
                                target.style.display = 'none';
                                const fallback = target.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-red flex items-center justify-center border border-stroke-primary"
                            style={{ display: comment.avatar_url ? 'none' : 'flex' }}
                          >
                            <span className="text-white text-xs font-bold uppercase">
                              {comment.author.charAt(0)}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                              <span className="text-text-primary text-sm font-bold">{comment.author}</span>
                              {user && comment.user_id === user.id && (
                                <span className="text-brand-red text-xs font-bold uppercase tracking-wide">You</span>
                              )}
                              <span className="text-text-muted text-xs">{formatTimestamp(comment.created_at)}</span>
                              {comment.updated_at && !comment.is_deleted && (
                                <span className="text-text-muted text-xs italic">(edited)</span>
                              )}
                            </div>
                            {editingId === comment.id ? (
                              <div className="flex gap-2 mt-1">
                                <input
                                  type="text"
                                  value={editText}
                                  onChange={(e) => setEditText(e.target.value)}
                                  maxLength={500}
                                  className="flex-1 bg-surface-secondary border border-stroke-primary text-text-primary text-sm px-3 py-1.5 rounded-[4px] placeholder-text-muted focus:outline-none focus:border-brand-red transition-colors"
                                  autoFocus
                                />
                                <button
                                  onClick={() => handleSaveEdit(comment.id, null)}
                                  disabled={!editText.trim() || savingEdit}
                                  className="bg-brand-red text-white text-xs font-bold uppercase px-3 py-1.5 rounded-[4px] hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                                >
                                  {savingEdit ? 'Saving…' : 'Save'}
                                </button>
                                <button
                                  onClick={() => { setEditingId(null); setEditText(''); }}
                                  className="text-text-muted hover:text-text-secondary text-xs transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <p className={`text-sm leading-relaxed break-words ${comment.is_deleted ? 'text-text-muted italic' : 'text-text-secondary'}`}>
                                {comment.is_deleted ? '[deleted]' : comment.body}
                              </p>
                            )}
                            {!comment.is_deleted && editingId !== comment.id && (
                              <div className="flex items-center gap-3 mt-2 flex-wrap tap-compact">
                                {/* Comment Like Button */}
                                <button
                                  onClick={() => handleCommentLike(comment.id)}
                                  disabled={commentLikeLoading === comment.id}
                                  className={`flex items-center gap-1 text-xs transition-colors ${
                                    cLike.liked
                                      ? 'text-brand-red' :'text-text-muted hover:text-brand-red'
                                  } disabled:opacity-60`}
                                  aria-label={cLike.liked ? 'Unlike comment' : 'Like comment'}
                                  title={!user ? 'Sign in to like' : cLike.liked ? 'Unlike' : 'Like'}
                                >
                                  <svg
                                    className="w-3.5 h-3.5"
                                    fill={cLike.liked ? 'currentColor' : 'none'}
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                                  </svg>
                                  {cLike.count > 0 && <span>{cLike.count}</span>}
                                </button>
                                <button
                                  onClick={() => {
                                    setReplyingTo(replyingTo === comment.id ? null : comment.id);
                                    setReplyText('');
                                  }}
                                  className="text-text-muted hover:text-brand-red text-xs transition-colors font-medium uppercase tracking-wide"
                                >
                                  Reply
                                </button>
                                {comment.replies && comment.replies.length > 0 && (
                                  <button
                                    onClick={() => toggleReplies(comment.id)}
                                    className="text-brand-red hover:text-red-400 text-xs transition-colors font-medium"
                                  >
                                    {expandedReplies.has(comment.id)
                                      ? '▲ Hide replies'
                                      : `▼ ${comment.replies.length} ${comment.replies.length === 1 ? 'reply' : 'replies'}`}
                                  </button>
                                )}
                                {user && comment.user_id === user.id && (
                                  <span className="flex items-center gap-2 ml-auto">
                                    <button
                                      onClick={() => handleStartEdit(comment)}
                                      className="flex items-center gap-1 text-text-muted hover:text-brand-red text-xs transition-colors"
                                      aria-label="Edit comment"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002-2h11a2 2 0 002 2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                      Edit
                                    </button>
                                    {confirmDeleteId === comment.id ? (
                                      <span className="flex items-center gap-1.5">
                                        <span className="text-text-muted text-xs">Delete?</span>
                                        <button
                                          onClick={() => handleDeleteComment(comment.id, null)}
                                          disabled={deletingId === comment.id}
                                          className="text-red-500 hover:text-red-400 text-xs font-bold transition-colors disabled:opacity-40"
                                        >
                                          {deletingId === comment.id ? 'Deleting…' : 'Yes'}
                                        </button>
                                        <button
                                          onClick={() => setConfirmDeleteId(null)}
                                          className="text-text-muted hover:text-text-secondary text-xs transition-colors"
                                        >
                                          No
                                        </button>
                                      </span>
                                    ) : (
                                      <button
                                        onClick={() => setConfirmDeleteId(comment.id)}
                                        className="flex items-center gap-1 text-text-muted hover:text-red-500 text-xs transition-colors"
                                        aria-label="Delete comment"
                                      >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Delete
                                      </button>
                                    )}
                                  </span>
                                )}
                              </div>
                            )}
                            {replyingTo === comment.id && (
                              <form
                                onSubmit={(e) => handleSubmitReply(e, comment.id)}
                                className="mt-3 flex gap-2"
                              >
                                <input
                                  type="text"
                                  value={replyText}
                                  onChange={(e) => setReplyText(e.target.value)}
                                  placeholder={`Reply to ${comment.author}...`}
                                  maxLength={300}
                                  className="flex-1 bg-surface-secondary border border-stroke-primary text-text-primary text-xs px-2 py-1.5 rounded-[4px] placeholder-text-muted focus:outline-none focus:border-brand-red transition-colors"
                                />
                                <button
                                  type="submit"
                                  disabled={!replyText.trim() || submitting}
                                  className="bg-brand-red text-white text-xs font-bold uppercase px-3 py-1.5 rounded-[4px] hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                  Post
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setReplyingTo(null)}
                                  className="text-text-muted hover:text-text-secondary text-xs px-2 transition-colors"
                                >
                                  Cancel
                                </button>
                              </form>
                            )}

                            {comment.replies && comment.replies.length > 0 && expandedReplies.has(comment.id) && (
                              <ul className="mt-3 space-y-3 border-l-2 border-stroke-primary pl-4">
                                {comment.replies.map((reply) => {
                                  const rLike = commentLikes[reply.id] ?? { count: 0, liked: false };
                                  return (
                                    <li key={reply.id} className="flex gap-2">
                                      {reply.avatar_url ? (
                                        <img
                                          src={reply.avatar_url}
                                          alt={`${reply.author}'s avatar`}
                                          className="flex-shrink-0 w-6 h-6 rounded-full object-cover border border-stroke-primary"
                                          onError={(e) => {
                                            const target = e.currentTarget as HTMLImageElement;
                                            target.style.display = 'none';
                                            const fallback = target.nextElementSibling as HTMLElement;
                                            if (fallback) fallback.style.display = 'flex';
                                          }}
                                        />
                                      ) : null}
                                      <div
                                        className="flex-shrink-0 w-6 h-6 rounded-full bg-surface-tertiary border border-stroke-primary flex items-center justify-center"
                                        style={{ display: reply.avatar_url ? 'none' : 'flex' }}
                                      >
                                        <span className="text-text-muted text-xs font-bold uppercase">
                                          {reply.author.charAt(0)}
                                        </span>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline gap-2 mb-0.5">
                                          <span className="text-text-primary text-xs font-bold">{reply.author}</span>
                                          {user && reply.user_id === user.id && (
                                            <span className="text-brand-red text-xs font-bold uppercase tracking-wide">You</span>
                                          )}
                                          <span className="text-text-muted text-xs">{formatTimestamp(reply.created_at)}</span>
                                          {reply.updated_at && !reply.is_deleted && (
                                            <span className="text-text-muted text-xs italic">(edited)</span>
                                          )}
                                        </div>
                                        {editingId === reply.id ? (
                                          <div className="flex gap-2 mt-1">
                                            <input
                                              type="text"
                                              value={editText}
                                              onChange={(e) => setEditText(e.target.value)}
                                              maxLength={300}
                                              className="flex-1 bg-surface-secondary border border-stroke-primary text-text-primary text-xs px-2 py-1.5 rounded-[4px] placeholder-text-muted focus:outline-none focus:border-brand-red transition-colors"
                                              autoFocus
                                            />
                                            <button
                                              onClick={() => handleSaveEdit(reply.id, comment.id)}
                                              disabled={!editText.trim() || savingEdit}
                                              className="bg-brand-red text-white text-xs font-bold uppercase px-3 py-1.5 rounded-[4px] hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                                            >
                                              {savingEdit ? 'Saving…' : 'Save'}
                                            </button>
                                            <button
                                              onClick={() => { setEditingId(null); setEditText(''); }}
                                              className="text-text-muted hover:text-text-secondary text-xs px-2 transition-colors"
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                        ) : (
                                          <p className={`text-xs leading-relaxed break-words ${reply.is_deleted ? 'text-text-muted italic' : 'text-text-secondary'}`}>
                                            {reply.is_deleted ? '[deleted]' : reply.body}
                                          </p>
                                        )}
                                        {!reply.is_deleted && editingId !== reply.id && (
                                          <div className="flex items-center gap-3 mt-1 flex-wrap tap-compact">
                                            {/* Reply Like Button */}
                                            <button
                                              onClick={() => handleCommentLike(reply.id)}
                                              disabled={commentLikeLoading === reply.id}
                                              className={`flex items-center gap-1 text-xs transition-colors ${
                                                rLike.liked
                                                  ? 'text-brand-red' :'text-text-muted hover:text-brand-red'
                                              } disabled:opacity-60`}
                                              aria-label={rLike.liked ? 'Unlike reply' : 'Like reply'}
                                              title={!user ? 'Sign in to like' : rLike.liked ? 'Unlike' : 'Like'}
                                            >
                                              <svg
                                                className="w-3 h-3"
                                                fill={rLike.liked ? 'currentColor' : 'none'}
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                                              </svg>
                                              {rLike.count > 0 && <span>{rLike.count}</span>}
                                            </button>
                                            {user && reply.user_id === user.id && (
                                              <span className="flex items-center gap-2 ml-auto">
                                                <button
                                                  onClick={() => handleStartEdit(reply)}
                                                  className="flex items-center gap-1 text-text-muted hover:text-brand-red text-xs transition-colors"
                                                  aria-label="Edit reply"
                                                >
                                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002-2h11a2 2 0 002 2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                  </svg>
                                                  Edit
                                                </button>
                                                {confirmDeleteId === reply.id ? (
                                                  <span className="flex items-center gap-1.5">
                                                    <span className="text-text-muted text-xs">Delete?</span>
                                                    <button
                                                      onClick={() => handleDeleteComment(reply.id, comment.id)}
                                                      disabled={deletingId === reply.id}
                                                      className="text-red-500 hover:text-red-400 text-xs font-bold transition-colors disabled:opacity-40"
                                                    >
                                                      {deletingId === reply.id ? 'Deleting…' : 'Yes'}
                                                    </button>
                                                    <button
                                                      onClick={() => setConfirmDeleteId(null)}
                                                      className="text-text-muted hover:text-text-secondary text-xs transition-colors"
                                                    >
                                                      No
                                                    </button>
                                                  </span>
                                                ) : (
                                                  <button
                                                    onClick={() => setConfirmDeleteId(reply.id)}
                                                    className="flex items-center gap-1 text-text-muted hover:text-red-500 text-xs transition-colors"
                                                    aria-label="Delete reply"
                                                  >
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                    Delete
                                                  </button>
                                                )}
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
      <AuthModal
        isOpen={showAuthPrompt}
        onClose={() => setShowAuthPrompt(false)}
        defaultMode="login"
      />
      {/* Report/Flag Modal */}
      {showReportModal && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black bg-opacity-70 px-4"
          onClick={(e) => e.target === e.currentTarget && handleCloseReport()}
        >
          <div className="bg-surface-primary border border-stroke-primary rounded-[4px] w-full max-w-md shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-stroke-primary">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21V5a1 1 0 011-1h10.586a1 1 0 01.707.293l1.414 1.414A1 1 0 0017.414 6H21a1 1 0 01.894 1.447l-3 6a1 1 0 01-.894.553H3" />
                </svg>
                <span className="text-text-primary font-bold uppercase tracking-wider text-sm">Report Video</span>
              </div>
              <button
                onClick={handleCloseReport}
                className="text-text-secondary hover:text-text-primary transition-colors p-1"
                aria-label="Close report dialog"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-5">
              {reportSuccess ? (
                <div className="flex flex-col items-center gap-3 py-4 text-center">
                  <div className="w-12 h-12 rounded-full bg-green-500 bg-opacity-10 flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-text-primary font-bold">Report Submitted</p>
                  <p className="text-text-secondary text-sm">Thank you for helping keep our community safe. We&apos;ll review this content shortly.</p>
                  <button
                    onClick={handleCloseReport}
                    className="mt-2 bg-brand-red text-white text-sm font-bold uppercase px-5 py-2 rounded-[4px] hover:bg-red-700 transition-colors"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmitReport}>
                  <p className="text-text-secondary text-sm mb-4">
                    Select a reason for reporting <span className="text-text-primary font-medium">&ldquo;{video.title}&rdquo;</span>
                  </p>

                  {/* Category selection */}
                  <div className="space-y-2 mb-4">
                    {[
                      { value: 'spam', label: 'Spam or misleading' },
                      { value: 'violence', label: 'Violent or graphic content' },
                      { value: 'hate_speech', label: 'Hate speech or harassment' },
                      { value: 'sexual_content', label: 'Sexual or explicit content' },
                      { value: 'misinformation', label: 'Misinformation or false news' },
                      { value: 'copyright', label: 'Copyright infringement' },
                      { value: 'other', label: 'Other' },
                    ].map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-[4px] border cursor-pointer transition-colors ${
                          reportCategory === option.value
                            ? 'border-orange-500 bg-orange-500 bg-opacity-10' :'border-stroke-primary hover:border-text-muted'
                        }`}
                      >
                        <input
                          type="radio"
                          name="reportCategory"
                          value={option.value}
                          checked={reportCategory === option.value}
                          onChange={(e) => setReportCategory(e.target.value)}
                          className="accent-orange-500"
                        />
                        <span className="text-text-primary text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Optional details */}
                  <div className="mb-4">
                    <label className="block text-text-secondary text-xs uppercase tracking-wide mb-1.5">
                      Additional details (optional)
                    </label>
                    <textarea
                      value={reportDetails}
                      onChange={(e) => setReportDetails(e.target.value)}
                      placeholder="Describe the issue..."
                      maxLength={500}
                      rows={3}
                      className="w-full bg-surface-secondary border border-stroke-primary text-text-primary text-sm px-3 py-2 rounded-[4px] placeholder-text-muted focus:outline-none focus:border-orange-500 transition-colors resize-none"
                    />
                  </div>

                  {reportError && (
                    <p className="text-red-500 text-xs mb-3">{reportError}</p>
                  )}

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleCloseReport}
                      className="flex-1 bg-surface-secondary border border-stroke-primary text-text-secondary text-sm font-bold uppercase px-4 py-2 rounded-[4px] hover:text-text-primary transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!reportCategory || reportSubmitting}
                      className="flex-1 bg-orange-500 text-white text-sm font-bold uppercase px-4 py-2 rounded-[4px] hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      {reportSubmitting ? 'Submitting...' : 'Submit Report'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
